import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { UserProfile } from "@/types/user";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useDashboardData } from "@/components/dashboard/useDashboardData";

type CreateChatDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreateNewChat: (member: UserProfile) => void;
  currentUserId: string;
};

export function CreateChatDialog({
  open,
  onOpenChange,
  onCreateNewChat,
  currentUserId,
}: CreateChatDialogProps) {
  const [selectedMember, setSelectedMember] = useState<UserProfile | null>(
    null
  );
  const { teamMembers } = useDashboardData();

  const availableMembers = teamMembers.filter(
    (member) => member.id !== currentUserId
  );

  useEffect(() => {
    if (!open) {
      setSelectedMember(null);
    }
  }, [open]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (selectedMember) {
      onCreateNewChat(selectedMember);
      onOpenChange(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>Create New Chat</DialogTitle>
            <DialogDescription>
              Start a conversation with a team member.
            </DialogDescription>
          </DialogHeader>

          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-4 items-start gap-4">
              <Label className="text-right pt-2">Members</Label>
              <ScrollArea className="h-[200px] col-span-3 border rounded-md p-2">
                <RadioGroup
                  value={selectedMember?.id || ""}
                  onValueChange={(value) => {
                    const member = availableMembers.find((m) => m.id === value);
                    setSelectedMember(member || null);
                  }}
                >
                  {availableMembers.length === 0 ? (
                    <p className="text-sm text-muted-foreground py-2 text-center">
                      No team members available
                    </p>
                  ) : (
                    availableMembers.map((member) => (
                      <div
                        key={member.id}
                        className="flex items-center space-x-2 py-1"
                      >
                        <RadioGroupItem
                          value={member.id}
                          id={`member-${member.id}`}
                        />
                        <div className="flex items-center gap-2">
                          <Avatar className="h-8 w-8">
                            <AvatarImage src={member.avatarUrl} />
                            <AvatarFallback>
                              {member.name.slice(0, 2).toUpperCase()}
                            </AvatarFallback>
                          </Avatar>
                          <Label
                            htmlFor={`member-${member.id}`}
                            className="cursor-pointer flex-1"
                          >
                            {member.name}
                          </Label>
                        </div>
                      </div>
                    ))
                  )}
                </RadioGroup>
              </ScrollArea>
            </div>
          </div>

          <DialogFooter>
            <Button type="submit" disabled={!selectedMember}>
              Create Chat
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
